import React, { useEffect, useState } from 'react';
import apiCall from '../helper/apiCall';
import { toast } from 'react-toastify';
import ArtistForm from '../components/ArtistForm';
import Modal from '../components/Modal';

// Using your defined VPS URL for image paths
const VPS_BASE_URL = "https://www.kanthasri.com";

const Artist = () => {
    const [artists, setArtists] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalType, setModalType] = useState('add');
    const [artistToEdit, setArtistToEdit] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const fetchArtists = async () => {
        setIsLoading(true);
        try {
            const result = await apiCall('getArtists');
            if (result.success && result.data) {
                setArtists(result.data);
            } else {
                setArtists([]);
                toast.error("Failed to fetch artists.");
            }
        } catch (error) {
            console.error("Failed to fetch artists:", error);
            toast.error("Error fetching artists.");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchArtists();
    }, []);

    const handleAddArtist = () => {
        setModalType('add');
        setIsModalOpen(true);
        setArtistToEdit(null);
    };

    const handleEditArtist = (artist) => {
        setModalType('edit');
        setIsModalOpen(true);
        setArtistToEdit(artist);
    };

    const handleSaveArtist = async (newArtist) => {
        let result;
        if (modalType === 'add') {
            result = await apiCall('createArtist', newArtist);
        } else {
            result = await apiCall('updateArtist', newArtist);
        }
        
        if (result.success) {
            fetchArtists(); 
            toast.success(`Artist ${modalType === 'add' ? 'created' : 'updated'} successfully!`);
            setIsModalOpen(false);
        } else {
            toast.error(result.message || `Failed to ${modalType === 'add' ? 'create' : 'update'} artist.`);
        }
    };

    return (
        <div className="p-8 font-sans bg-gray-100 min-h-screen">
            <h1 className="text-3xl font-extrabold text-gray-900 mb-6">Artist Management</h1>
            <div className="flex justify-between items-center mb-6">
                <span className="text-lg font-semibold text-gray-700">All Artists</span>
                <button
                    onClick={handleAddArtist}
                    className="bg-indigo-600 text-white px-4 py-2 rounded-lg shadow-md hover:bg-indigo-700 transition-colors"
                >
                    Add Artist
                </button>
            </div>

            {isLoading ? (
                <div className="text-center py-8 text-gray-500">Loading artists...</div>
            ) : (
                <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-200">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Profile</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Artist Name</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Mobile</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Address</th>
                                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {artists.length > 0 ? (
                                    artists.map(artist => {
                                        // Logic to handle JSONB profile picture structure
                                        const profilePic = artist.profile_picture?.thumbnail 
                                            ? `${VPS_BASE_URL}${artist.profile_picture.thumbnail}` 
                                            : 'https://via.placeholder.com/40';

                                        // Logic to handle nested address relation
                                        const fullAddress = artist.address 
                                            ? `${artist.address.address_line_1}, ${artist.address.city} - ${artist.address.pincode}`
                                            : 'No address provided';

                                        return (
                                            <tr key={artist.id} className="hover:bg-gray-50 transition-colors">
                                                <td className="px-6 py-4 whitespace-nowrap">
                                                    <img 
                                                        src={profilePic} 
                                                        alt={artist.name} 
                                                        className="h-10 w-10 rounded-full object-cover border"
                                                    />
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{artist.name}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{artist.mobile}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{artist.email}</td>
                                                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate" title={fullAddress}>
                                                    {fullAddress}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                    <button
                                                        onClick={() => handleEditArtist(artist)}
                                                        className="text-indigo-600 hover:text-indigo-900"
                                                    >
                                                        Edit
                                                    </button>
                                                </td>
                                            </tr>
                                        );
                                    })
                                ) : (
                                    <tr>
                                        <td colSpan="6" className="px-6 py-4 text-center text-gray-500">No artists found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <Modal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                title={modalType === 'add' ? 'Add New Artist' : 'Edit Artist'}
            >
                <ArtistForm
                    initialData={artistToEdit}
                    onSave={handleSaveArtist}
                    onClose={() => setIsModalOpen(false)}
                />
            </Modal>
        </div>
    );
};

export default Artist;